import React, { useState, useEffect } from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { apiClient } from '@/App';
import { toast } from 'sonner';

const PaymentMethodSelector = ({ 
  onStripeCheckout, 
  onPaymentSuccess,
  paymentData,
  isProcessing,
  setIsProcessing,
  amount = 8.90
}) => {
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Récupérer la config PayPal au montage
  useEffect(() => {
    const fetchPayPalConfig = async () => {
      try {
        const response = await apiClient.get('/payment/paypal/config');
        if (response.data.enabled) {
          setPaypalConfig(response.data);
        }
      } catch (error) {
        console.log('PayPal non disponible');
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchPayPalConfig();
  }, []);

  const handleStripeClick = () => {
    setSelectedMethod('stripe');
    onStripeCheckout();
  };

  const createPayPalOrder = async () => {
    setIsProcessing(true);
    try {
      const response = await apiClient.post('/payment/paypal/create-order', {
        debiteur_nom: paymentData.debiteur_nom,
        debiteur_email: paymentData.debiteur_email,
        montant: paymentData.montant,
        copropriete_nom: paymentData.copropriete_nom,
        lettre_content: paymentData.lettre_content,
        coproprietaire_id: paymentData.coproprietaire_id
      });
      
      // Retourner l'ID de la commande PayPal pour le SDK
      return response.data.order_id;
    } catch (error) {
      console.error('Erreur création commande PayPal:', error);
      toast.error('Erreur lors de la création de la commande PayPal');
      setIsProcessing(false);
      throw error;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      const response = await apiClient.post(`/payment/paypal/capture/${data.paymentID}`, null, {
        params: { payer_id: data.payerID }
      });
      
      if (response.data.status === 'success') {
        toast.success('Paiement PayPal réussi !');
        if (onPaymentSuccess) {
          onPaymentSuccess(response.data.commande_id);
        }
      }
    } catch (error) {
      console.error('Erreur capture PayPal:', error);
      toast.error('Erreur lors de la finalisation du paiement');
    } finally {
      setIsProcessing(false);
    }
  };

  const onPayPalError = (err) => {
    console.error('Erreur PayPal:', err);
    toast.error('Erreur PayPal - veuillez réessayer');
    setIsProcessing(false);
  };

  const onPayPalCancel = () => {
    toast.info('Paiement annulé');
    setIsProcessing(false);
  };

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        Choisissez votre méthode de paiement
      </h3>

      {/* Montant */}
      <div className="bg-slate-50 rounded-xl p-4 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-slate-600">Montant à payer</span>
          <span className="text-2xl font-bold text-emerald-600">{amount.toFixed(2)} €</span>
        </div>
        <p className="text-sm text-slate-500 mt-1">Envoi recommandé électronique Clearbus</p>
      </div>

      {/* Options de paiement */}
      <div className="grid gap-3">
        {/* Option Stripe (Carte bancaire) */}
        <button
          onClick={() => setSelectedMethod('stripe')}
          disabled={isProcessing}
          className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
            selectedMethod === 'stripe'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 hover:border-blue-300'
          } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              selectedMethod === 'stripe' ? 'bg-blue-500 text-white' : 'bg-slate-100'
            }`}>
              <CreditCard className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900">Carte bancaire</h4>
              <p className="text-sm text-slate-500">Visa, Mastercard, American Express</p>
            </div>
            <div className="flex gap-1">
              <img src="https://cdn.jsdelivr.net/gh/nicehorse06/iconify-payment-icons@1.0.0/icons/visa.svg" alt="Visa" className="h-6" />
              <img src="https://cdn.jsdelivr.net/gh/nicehorse06/iconify-payment-icons@1.0.0/icons/mastercard.svg" alt="Mastercard" className="h-6" />
            </div>
          </div>
        </button>

        {/* Option PayPal (si disponible) */}
        {paypalConfig && paypalConfig.enabled && (
          <button
            onClick={() => setSelectedMethod('paypal')}
            disabled={isProcessing}
            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
              selectedMethod === 'paypal'
                ? 'border-yellow-500 bg-yellow-50'
                : 'border-slate-200 hover:border-yellow-300'
            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                selectedMethod === 'paypal' ? 'bg-[#003087] text-white' : 'bg-slate-100'
              }`}>
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900">PayPal</h4>
                <p className="text-sm text-slate-500">Payez avec votre compte PayPal</p>
              </div>
              <img src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg" alt="PayPal" className="h-6 rounded" />
            </div>
          </button>
        )}
      </div>

      {/* Bouton de paiement Stripe */}
      {selectedMethod === 'stripe' && (
        <Button
          onClick={handleStripeClick}
          disabled={isProcessing}
          className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-lg font-semibold gap-2 mt-4"
          data-testid="pay-with-stripe-btn"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Redirection en cours...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Payer {amount.toFixed(2)} € par carte
            </>
          )}
        </Button>
      )}

      {/* Boutons PayPal */}
      {selectedMethod === 'paypal' && paypalConfig && paypalConfig.enabled && (
        <div className="mt-4">
          <PayPalScriptProvider options={{ 
            "client-id": paypalConfig.client_id,
            currency: "EUR"
          }}>
            <PayPalButtons
              style={{ 
                layout: "vertical",
                color: "gold",
                shape: "rect",
                label: "pay",
                height: 55
              }}
              disabled={isProcessing}
              createOrder={createPayPalOrder}
              onApprove={onPayPalApprove}
              onError={onPayPalError}
              onCancel={onPayPalCancel}
            />
          </PayPalScriptProvider>
        </div>
      )}

      {/* Sécurité */}
      <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mt-4">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Paiement 100% sécurisé
      </div>
    </div>
  );
};

export default PaymentMethodSelector;
